<script>
  import { push, querystring } from "svelte-spa-router"
  import { onMount, onDestroy } from "svelte"
  import { tweened } from "svelte/motion"
  import { cubicOut } from "svelte/easing"
  import Footer from "../../../../../Shared/footer.svelte"
  import Navbar from "../../../../../Shared/Navbar/index.svelte"
  import FilePage from "./Components/filePage.svelte"
  import Counter from "../../Shared/counter2.svelte"
  import notify from "../../../../../Script/notify"
  import bulkJson from "../../Stores/jsonBulkData"
  import Tab from "../../Shared/tab.svelte"
  import Loader from "../../../../../Shared/Slots/loader.svelte"
  import DocStatusContent from "./Components/docStatusContent.svelte"
  import { flatenObject } from "../../../../../Script/flatenObject"
  import { generateHTMLContent } from "../../../../../Script/generateHTMLContent"
  import ApplicationState from "../../../../../Stores/applicationStates"
  import { queryStringToJSON } from "../../../../../Script/queryString"
  import { fetchBulkStatus, fetchBulkTemplate, fetchSelectedDocumentTemp, fetchEmailTemplate, sendEmail } from "../../Script/bulkUpload"

  /**
   * @type {Array<import("../../Script/document").bulkDocument>}
   */
  let jsonData = []
  /**
   * @type {Array<import("../../Script/document").bulkDocument>}
   */
  let emailInProcess = []
  /**
   * @type {"Document Status"|"Emails"} active tab while generating files
   */
  let activeItem = "Document Status"
  /**
   * @type {"start"| "processing"|"completed"|"retry"} active item in document status tab
   */
  let activeDoc = "start"
  /**
   * @type {Number} number of emails to be send to the receiver
   */
  let sentEmails = 0
  /**
   * @type {Number} number of files failed to upload
   */
  let unsuccessfulEmails = 0
  /**
   * @type {import("../../../../../Script/types").EmailTemplate}
   */
  let emailData = {}
  /**
   * @type {Number} the total number of documents to be uploaded
   */
  let totalDoc = 0
  /**
   * @type {Number} the count which reflects the increment limit of the progress bar
   */
  let incrementLimit
  /**
   * @type {String} activeColor of the tabs
   */
  let activeColor
  /**
   * @type {Number} initial width of the progress bar
   */
  let width = 0
  /**
   * @type {Boolean} whether to show toggle or not
   */
  let showLoading = false
  /**
   * @type {String} the text to be shown while loading
   */
  let loadingText
  /**
   * @type {Boolean} name of the start button
   */
  let startBtnName = "Send Email"
  /**
   * @type {import("../../Script/document".Tab)}
   */
  let TabList = [
    { name: "Document Status", icon1: "fa-circle-check", icon2: "fa-clock" },
    { name: "Emails", icon1: "fa-envelope-circle-check", icon2: "fa-envelope" },
  ]
  /**
   * @type {Object} stores the query value in the current url location
   */
  let query
  /**
   * Creates a tweened value representing the width of a progress indicator.
   *
   * @param {number} initialValue - The initial value of the tweened width.
   * @param {Object} options - Options for configuring the tween animation.
   * @param {number} options.duration - The duration of the tween animation in milliseconds.
   * @param {function} options.easing - The easing function used for the animation.
   * @returns {Object} - A tweened value object that represents the width of the progress indicator.
   */
  const widthofProgress = tweened(0, {
    duration: 400,
    easing: cubicOut,
  })
  /**
   * @type {String} bulk ID
   */
  let bulkID

  let emailCC = ""

  onMount(async () => {
    try {
      // Set showLoading to true to indicate loading state
      showLoading = true
      // Set loadingText to indicate the current loading step
      loadingText = "Fetching Bulk Upload data&nbsp;&nbsp;"
      // Parse the query string into a JavaScript object
      query = queryStringToJSON($querystring)

      // Redirect to the bulk publishing page if the "id" parameter is missing in the query
      if (!query.id) {
        push("/publish/bulk")
        return
      }

      // Fetch data about the bulk publishing status using the "id" from the query
      let apiData = await fetchBulkStatus(query.id)

      // Redirect if email sending is not required
      if (!apiData.sendEmail) {
        push("/publish/bulk")
        return
      }

      // Redirect to view report if the status is "revoked"
      if (apiData.status == "revoked") {
        push(`/documents/bulk/view-report?id=${query.id}`)
        // Notify the user that an email has already been sent
        notify.info("You can't send email as you've already revoked all the documents.")
        return
      }

      // Redirect to view report if the status is "active"
      if (apiData.status == "active") {
        push(`/documents/bulk/view-report?id=${query.id}`)
        // Notify the user that an email has already been sent
        notify.info("You've already sent an email. Please reset the email to send it again.")
        return
      }

      // Redirect to sign & publish if the status is "uploaded"
      if (apiData.status == "uploaded") {
        push(`/publish/bulk/sign-publish?id=${query.id}`)
        return
      }

      // Handle cases where the status is "started"
      if (apiData.status == "started") {
        // Redirect to upload file or generate file page based on the "generateFile" flag
        if (!apiData.generateFile) push(`/publish/bulk/upload-file?id=${query.id}`)
        else push(`/publish/bulk/generate-file?id=${query.id}`)
        return
      }

      // Fetch bulk template data using the "id" from the query
      let bulkData = await fetchBulkTemplate(query.id)

      // Update the $bulkJson array with documents from the fetched bulk data
      $bulkJson = [...bulkData.documents]

      bulkID = bulkData.bulk.id

      if(bulkData.bulk.cc) emailCC = bulkData.bulk.cc

      // Initialize the "isEmailFailed" property for each document
      for (let i = 0; i < $bulkJson.length; i++) {
        $bulkJson[i].isEmailFailed = false
      }

      // Set the totalDoc variable to the total number of documents
      totalDoc = $bulkJson.length

      // Filter documents that do not have an "emailState"
      let tobeProcessed = $bulkJson.filter((json) => !json.emailState)

      // Update the startBtnName based on whether any emails have been sent
      if (tobeProcessed.length != $bulkJson.length) startBtnName = "Resume"

      // Update the count of sent emails
      sentEmails = $bulkJson.length - tobeProcessed.length

      // Calculate the increment limit for progress
      incrementLimit = 100 / $bulkJson.length

      // Change the progress bar width based on sent and unsuccessful emails
      changeWidth(sentEmails + unsuccessfulEmails)

      // Fetch data about the document template
      let documentData = await fetchSelectedDocumentTemp(bulkData.bulk.documentTemplate)

      // Fetch email template data
      if (documentData) {
        if (!documentData.email) throw "We couldn't find the email template associated with the document template."
        emailData = await fetchEmailTemplate(documentData.email)
      }

      // Update the JSON data with documents to be processed
      jsonData = [...tobeProcessed]
    } catch (error) {
      const info = "PAGE: Bulk Email\nEVENT: onMount"
      notify.danger(error, info)

      // Redirect to the bulk publishing page in case of an error
      push("/publish/bulk")
    } finally {
      // Set showLoading back to false to end the loading state
      showLoading = false
    }
  })

  /**
   * Function for initiating the whole process
   */
  const initiateProcess = () => {
    // Filter the $bulkJson array to get documents that haven't had emails sent.
    let tobeProcessed = $bulkJson.filter((a) => !a.emailState && !a.isEmailFailed)

    // Check if there's data in jsonData, documents to be processed, or emails currently in process.
    if (jsonData.length || tobeProcessed.length || emailInProcess.length) {
      // Call the function assignData2Array() to assign data to an array (assuming it's defined elsewhere).
      assignData2Array()
    } else {
      clearInterval(timer)
      console.timeEnd("initiate")
    }
  }

  onDestroy(() => {
    clearInterval(timer)
  })

  /**
   * Function for assigning data to respective array
   */
  const assignData2Array = () => {
    // Get the number of available hardware threads or cores
    let hwc = window.navigator.hardwareConcurrency

    // Assign data to the emailInProcess array if it has fewer elements than the available hardware threads
    if (emailInProcess.length < hwc) {
      // Extract a portion of jsonData to fill up to (hwc - emailInProcess.length) elements
      let eArray = jsonData.splice(0, hwc - emailInProcess.length)

      // Add the extracted elements to the emailInProcess array
      emailInProcess = [...emailInProcess, ...eArray]

      // Process each element of dArray by calling the getDataHash function
      for (let i = 0; i < eArray.length; i++) {
        sendEmailProcess(eArray[i])
      }
    }

    // Initiate the process by requesting the next animation frame
    // window.requestAnimationFrame(initiateProcess)
  }

  /**
   * Function for initiating the email transmission
   * @param {import("../../Script/document").bulkDocument} data
   */
  const sendEmailProcess = async (data) => {
    try {
      // Parse the email content placeholders using the parseEmailContent function.
      let parsedContent = await parseEmailContent(emailData.content, data)

      // Prepare the request body for sending the email.
      let reqBody = {
        to: data.email,
        subject: emailData.subject,
        content: generateHTMLContent(emailData, parsedContent),
        documentID: data.id,
        tag: `${data.id}:${bulkID}:${$ApplicationState.issuerID}:bulkUpload`,
      }

      if(emailCC) reqBody.cc = emailCC

      // Call the sendEmail function to initiate the email sending process for the provided 'data'.
      await sendEmail(reqBody)

      // Find the document in $bulkJson that matches the processed data's ID.
      let tobeUpdated = $bulkJson.filter((a) => a.id == data.id)[0]

      // Update the emailState of the processed document to indicate it was sent.
      tobeUpdated.emailState = true

      // Replace the original document in $bulkJson with the updated one.
      $bulkJson = [...$bulkJson.filter((a) => a.id != data.id), tobeUpdated]

      // Increment the counter for successfully sent emails.
      sentEmails++
      changeWidth()
    } catch (error) {
      // If an error occurs during the email sending process, log the error to the console.
      // console.error(error)

      // Increment the counter for unsuccessful sent emails.
      // unsuccessfulEmails++

      let tobeUpdated = $bulkJson.filter((a) => a.id == data.id)[0]

      tobeUpdated.isEmailFailed = true
      tobeUpdated.errorMsg = error

      let tempJson = $bulkJson.filter((a) => a.id != data.id)

      $bulkJson = [...tempJson.filter((a) => a.isEmailFailed), tobeUpdated, ...tempJson.filter((a) => !a.isEmailFailed)]

      // Replace the old entry with the updated one in the '$bulkJson' array
      // $bulkJson = [tobeUpdated, ...$bulkJson.filter((a) => a.id != data.id)]

      unsuccessfulEmails = $bulkJson.filter((a) => a.isEmailFailed).length

      changeWidth()
    } finally {
      // If sending the email was unsuccessful, update relevant data and counters.
      // Remove the processed email data from the emailInProcess array.
      emailInProcess = emailInProcess.filter((a) => a.id != data.id)
    }
  }

  /**
   * Change the width of a progress bar based on the increment limit.
   * @param {number} times - The number of times to increment the width (default is 1).
   */
  const changeWidth = (times = 1) => {
    // Check if the increment limit exists
    if (incrementLimit) {
      // Increase the width by the increment limit
      width = width + incrementLimit * times
      // Set the updated width to a variable called "widthofProgress"
      widthofProgress.set(width)

      // Check if the width is rounded to 99% or higher
      if (Math.round(width) >= 99) {
        // Check if there are unsuccessful emails
        if (unsuccessfulEmails) {
          // Check if all emails have been sent or only unsuccessful emails remain
          if (totalDoc == sentEmails + unsuccessfulEmails || totalDoc == unsuccessfulEmails) {
            // Set the "activeDoc" variable to "retry" to indicate a retry state
            activeDoc = "retry"
          }
          return
        } else {
          // Check if all emails have been successfully sent
          if (totalDoc == sentEmails) {
            // Set the "activeDoc" variable to "completed" to indicate a completed state
            activeDoc = "completed"
          }
          return
        }
      }
    }
  }

  $: checkStatus(activeDoc)

  const checkStatus = async (activeDoc) => {
    try {
      if (activeDoc == "completed") {
        let apiData = await fetchBulkStatus(query.id)
        if (apiData.status == "published") window.location.reload()
      }
    } catch (error) {
      console.error(error)
      const info = "PAGE: Bulk Email\nEVENT: While checking bulk status reactively"
      notify.danger(error, info)
    }
  }

  /**
   * Function for parse the email content and matching the attribute with its values
   * @param {String} content email content
   * @param {Object} jsonData data used for matching the attribute values
   */
  const parseEmailContent = async (content, jsonData) => {
    // Flatten the JSON metadata object within jsonData using the flatenObject function.
    let flatObject = flatenObject(JSON.parse(jsonData.metadata))

    let options = JSON.parse(jsonData.options)

    flatObject = { ...flatObject, ...options, documentID: jsonData.id }

    // Extract unique editor values enclosed in {{ }} from the 'content'.
    let editorValues = Array.from(new Set(content.match(/(?<=\{\{).*?(?=\}\})/g)))

    const linkedInMatches = content.match(/\[\[(.*?)\]\]/g)

    if (linkedInMatches) {
      const linkedInValues = [...new Set(linkedInMatches.map((match) => match.slice(2, -2)))]

      for (let i = 0; i < linkedInValues.length; i++) {
        if (linkedInValues[i] == "cert_url") {
          let url = jsonData.url.replaceAll("=", "%3D")
          content = content.replaceAll(`[[${linkedInValues[i]}]]`, url)
        } else if (linkedInValues[i] == "cert_ID") {
          content = content.replaceAll(`[[${linkedInValues[i]}]]`, jsonData.id)
        } else if (linkedInValues[i] == "document_type") {
          content = content.replaceAll(`[[${linkedInValues[i]}]]`, flatObject["document_type"])
        } else if (linkedInValues[i] == "issue_Year") {
          content = content.replaceAll(`[[${linkedInValues[i]}]]`, new Date(flatObject["date"]).getFullYear())
        } else if (linkedInValues[i] == "issue_Month") {
          content = content.replaceAll(`[[${linkedInValues[i]}]]`, new Date(flatObject["date"]).getMonth() + 1)
        } else if (linkedInValues[i] == "expire_Year" && flatObject["expireOn"]) {
          content = content.replaceAll(`[[${linkedInValues[i]}]]`, new Date(flatObject["expireOn"]).getFullYear())
        } else if (linkedInValues[i] == "expire_Month" && flatObject["expireOn"]) {
          content = content.replaceAll(`[[${linkedInValues[i]}]]`, new Date(flatObject["expireOn"]).getMonth() + 1)
        }
      }
    }

    // Check if there are editor values and non-empty flatObject.
    if (editorValues.length && Object.keys(flatObject).length) {
      // Iterate through each editor value found in the 'content'.
      for (let i = 0; i < editorValues.length; i++) {
        if (editorValues[i] == "expireOn" && flatObject["expireOn"]) {
          let changedFormat = new Date(flatObject["expireOn"]).toDateString().slice(4)
          content = content.replaceAll("{{expireOn}}", changedFormat)
        } else if (editorValues[i] == "date") {
          let changedFormat = new Date(flatObject["date"]).toDateString().slice(4)
          content = content.replaceAll("{{date}}", changedFormat)
        }
        // If the corresponding flatObject property exists, replace the editor value with its value.
        else if (flatObject[editorValues[i]]) {
          content = content.replaceAll(`{{${editorValues[i]}}}`, flatObject[editorValues[i]])
        }
        // If the editor value is "url", replace it with a link to the jsonData's URL.
        else if (editorValues[i] == "url") {
          content = content.replaceAll(`{{url}}`, `<a href="${jsonData.url}" target="_blank" rel="noreferrer" style="color:inherit; overflow-wrap: break-word;" ><strong>${jsonData.url}</strong></a>`)
        }
        // If there's no matching data for the editor value, replace it with a strong tag.
        else {
          content = content.replaceAll(`{{${editorValues[i]}}}`, ``)
        }
      }
    }

    let qrSource = `https://utils.print2block.in/qr/create/?size=120&data=${jsonData.url}`

    content = replaceQRSource(content, qrSource)

    // Return the modified content with replaced placeholders.
    return content
  }

  const replaceQRSource = (emailContent, qrSource) => {
    try {
      let div = document.createElement("div")
      div.innerHTML = emailContent

      let imageElem = div.querySelectorAll(".qr")

      for (let i = 0; i < imageElem.length; i++) {
        if (imageElem[i]) {
          let newImage = document.createElement("img")
          newImage.src = qrSource
          newImage.className = "qr"
          newImage.alt = "document-QR"
          newImage.style.width = "120px"

          imageElem[i].replaceWith(newImage)

          emailContent = div.innerHTML
        }
      }

      return emailContent
    } catch (error) {
      throw error
    }
  }

  let timer

  const whileStart = async () => {
    console.time("initiate")

    // Set the activeDoc status to "processing"
    activeDoc = "processing"
    // Call the initiateProcess function
    // initiateProcess()

    timer = setInterval(initiateProcess, 30)
  }

  $: changeActiveColor(activeDoc)

  /**
   * Function for changing the active color of the tabs
   * @param {String} activeDoc the current state of the document status
   */
  const changeActiveColor = (activeDoc) => (activeColor = activeDoc == "completed" ? "#22C55E" : "#FF6A00")

  /**
   * Navigate to the report view page based on the bulk publishing status.
   */
  const navigateReport = async () => {
    try {
      showLoading = true
      // Fetch bulk publishing status using the "id" from the query
      await fetchBulkStatus(query.id)
    } catch (error) {
      const info = "PAGE: Bulk Email\nEVENT: While checking bulk status before navigating to report page"
      notify.danger(error, info)
    } finally {
      // Redirect to the bulk report page in case of an error
      showLoading = false
      push(`/documents/bulk/view-report?id=${query.id}`)
    }
  }
</script>

<svelte:head>
  <title>Bulk Email</title>
</svelte:head>

<div class="relative min-h-screen w-full overflow-hidden bg-[#f7f7f7] pb-24 dark:bg-bodyblack">
  <Navbar />

  <div class="flex px-6 pb-12 pt-9">
    <div class="relative mx-auto flex w-full max-w-[920px] flex-col justify-between rounded-md bg-white dark:bg-mdGray">
      <Tab {activeColor} bind:activeItem {activeDoc} {TabList} />
      {#if showLoading}
        <div class="flex h-full items-center justify-center text-base text-gray-400">
          <Loader>
            <span class="flex">
              {@html loadingText}
            </span>
          </Loader>
        </div>
      {:else}
        <div class="flex h-full w-full select-none flex-col items-center justify-center p-6 mobile:p-12">
          <div class="grid w-full grid-cols-3 justify-center gap-3 xl:gap-6">
            <Counter titleText="TOTAL<br>DOCUMENTS" span="col-span-3" count={totalDoc} color="#ff6a00" gradient="#f8a500, #ff6a00" icon="fa-file-lines" />
            <Counter titleText="SENT<br>EMAILS" span="col-span-3" count={sentEmails} color="#00D515" gradient="#58FE69, #00D515" icon="fa-envelope-circle-check" />
            <Counter titleText="FAILED<br>DELIVERIES" span="col-span-3" count={unsuccessfulEmails} color="#F03232" gradient="#F87A7A, #F03232" icon="fa-envelope-dot" />
          </div>
          <!-- <div class="mt-6 hidden w-full justify-center gap-6 md:flex">
            <Counter titleText="TOTAL<br>DOCUMENTS" count={totalDoc} innerColor="inset 0px 20px 40px 0px rgba(255, 118, 142, 0.3)" gradient="#ff43c0 0%, #ffa95c 100%" />
            <Counter titleText="SENT<br>EMAILS" count={sentEmails} innerColor="inset 0px 20px 60px 0px rgba(141,142,255,0.3)" gradient="#0db8ff 0%, #f332ff 100%" />
            <Counter titleText="UNSUCCESSFUL<br>EMAILS" count={unsuccessfulEmails} innerColor="inset 0px 20px 40px 0px rgba(255,81,47,0.3)" gradient="#FF512F 0%, #DD2476 100%" />
          </div>
          <div class=" flex w-full flex-col justify-center gap-6 md:hidden">
            <Counter titleText="TOTAL<br>DOCUMENTS" count={totalDoc} color={"#ff6a00"} gradient={"#f8a500, #ff6a00"} icon={"fa-solid fa-file-lines"} />
            <Counter titleText="SENT<br>EMAILS" count={sentEmails} color={"#00D515"} gradient={"#58FE69, #00D515"} icon={"fa-solid fa-envelope-circle-check"} />
            <Counter titleText="UNSUCCESSFUL<br>EMAILS" count={unsuccessfulEmails} color={"#fe036a"} gradient={"#f99dbc, #f5347f"} icon={"fa-solid fa-envelope-dot"} />
          </div> -->
          {#if activeItem == "Emails"}
            <FilePage />
          {:else}
            <DocStatusContent on:redirect={navigateReport} {startBtnName} {query} {activeDoc} width={$widthofProgress} on:click={whileStart} />
          {/if}
        </div>
      {/if}

      <!-- {#if activeItem == "Document Status"}
        <h1 class="select-none pb-6 text-center text-base font-medium text-red-500 mobile:pb-12">Please don't refresh the page while the process is in progress.</h1>
      {/if} -->
    </div>
  </div>

  <Footer />
</div>
